import React from 'react';
import EventActionsMenu from './EventActionsMenu';
import { eventStatusLabel, eventTimeRange, eventTypeName, formatEventDate } from './eventConstants';
import { rsvpProgress, summaryFor } from './eventsBoard';
import { TILE_CLASS } from './eventStyles';
import { attendanceSummary } from './checkin/checkin';

export function EventStatusPill({ status }) {
    const canceled = status === 'canceled';
    return (
        <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${canceled
            ? 'border-[var(--ui-border)] text-[var(--ink-muted)]'
            : 'border-[var(--selected-pill-border)] text-[var(--selected-pill-ink)]'}`}>
            {eventStatusLabel(status)}
        </span>
    );
}

function CalendarIcon() {
    return (
        <svg aria-hidden="true" viewBox="0 0 20 20" width="16" height="16" className="mt-0.5 shrink-0 text-[var(--ink-muted)]" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4.5" width="14" height="12.5" rx="2" />
            <path d="M3 8.5h14M7 2.75v3M13 2.75v3" />
        </svg>
    );
}

const CHIP_CLASS = 'inline-flex items-center rounded-full border border-[var(--ui-border)] px-2.5 py-0.5 text-xs font-medium text-[var(--ink)]';

// "RSVPs in" on the left, "x/y · NN%" on the right, over a thin teal bar. An event with no guests says so and the bar stays empty.
export function RsvpProgress({ counts }) {
    const progress = rsvpProgress(counts);
    return (
        <div data-testid="event-rsvp" className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-[var(--ink-muted)]">{progress.empty ? 'RSVPs' : 'RSVPs in'}</span>
                <span className="font-semibold text-[var(--ink)]" data-testid="event-rsvp-text">{progress.text}</span>
            </div>
            <div
                className="ia-evt-progress"
                role="progressbar"
                aria-label="RSVPs in"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress.width}
                aria-valuetext={progress.empty ? 'No guests yet' : `${progress.answered} of ${progress.invited} invited guests have answered`}
            >
                <span className="ia-evt-progress__fill" style={{ width: `${progress.width}%` }} />
            </div>
        </div>
    );
}

export default function EventCard({ event, kits, canEdit, onEdit, onCancel, onGuests, onPlan, rsvp = null, words, hotels = null, attendance = null }) {
    const canceled = event.engagement?.status === 'canceled';
    const counts = summaryFor(rsvp, event);
    // G5: "12 checked in · 10 of 40 expected · 2 walk-ins" once anyone is in, and the no-shows once the event is over
    const numbers = attendance?.get(event.engagement_id);
    const checkin = numbers && Number(numbers.heads) > 0 && (Number(numbers.checked_in) > 0 || numbers.is_over) && !canceled ? attendanceSummary(numbers) : null;
    const open = onPlan ? () => onPlan(event.engagement_id) : onGuests ? () => onGuests(event) : null;
    // T2: anywhere on the card opens the event, except the card's own buttons and menus.
    const cardClick = (click) => { if (open && !click.target.closest('button, a, input, select, textarea, [role="menu"], [role="menuitem"]')) open(); };
    const lastDay = event.end_date && event.end_date !== event.event_date ? ` to ${formatEventDate(event.end_date)}` : '';
    const when = [`${formatEventDate(event.event_date)}${lastDay}`, event.venue_name || 'Venue to be set'].filter(Boolean).join(' · ');
    const detail = [eventTimeRange(event), event.venue_address].filter(Boolean).join(' · ');
    return (
        <article
            data-testid="event-card"
            onClick={cardClick}
            className={`${TILE_CLASS} ia-evt-card p-4 pt-6 flex flex-col gap-3 ${open ? 'cursor-pointer' : ''} ${canceled ? 'ia-tile3d--muted' : ''}`}
        >
            <div className="flex items-start justify-between gap-3 min-w-0">
                <h3 className={`min-w-0 flex-1 font-bold text-[var(--ink)] break-words ${canceled ? 'line-through' : ''}`}>
                    {open
                        ? <button type="button" className="ia-evt-card-open" onClick={open} aria-label={`Open ${event.event_name}`} data-testid="event-card-open">{event.event_name}</button>
                        : event.event_name}
                </h3>
                <EventStatusPill status={event.engagement?.status} />
            </div>
            <div className="flex items-start gap-2 min-w-0 text-sm text-[var(--ink)]">
                <CalendarIcon />
                <div className="min-w-0">
                    <div className="break-words">{when}</div>
                    <div className="break-words text-xs text-[var(--ink-muted)]">{detail}</div>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span className={CHIP_CLASS} data-testid="event-type-chip">{eventTypeName(kits, event)}</span>
                {rsvp && <span className={CHIP_CLASS} data-testid="event-invited-chip">{counts.invited} invited</span>}
                {hotels?.get(event.engagement_id) && <span className={CHIP_CLASS} data-testid="event-hotel-chip" title={hotels.get(event.engagement_id).hotel_name}>🏨 Hotel block</span>}
                {event.expected_guest_count != null && <span className="text-xs text-[var(--ink-muted)]">{event.expected_guest_count} expected</span>}
            </div>
            {rsvp && <RsvpProgress counts={counts} />}
            {checkin && <div className="text-sm text-[var(--ink)]" data-testid="event-checkin">✓ {checkin.text}</div>}
            <div className="flex justify-end">
                <EventActionsMenu event={event} canEdit={canEdit} onEdit={onEdit} onCancel={onCancel} onGuests={onGuests} onPlan={onPlan} words={words} />
            </div>
        </article>
    );
}
